/**
 * AI Command — governance record exports.
 *
 * Two documents, both built for the reader who decides whether this
 * organization governs its AI: a market conduct examiner, an OIG style
 * reviewer, a URAC surveyor, or a client's diligence team.
 *
 *   1. exportUseCaseRecord — one system's complete governance record:
 *      intake facts and screens, risk tier, non waivable rules, the impact
 *      assessment of record, every committee decision with contingency
 *      status, the vendor dossier, the issue log, NIST AI RMF alignment.
 *   2. exportProgramReport — the whole program on paper: committee,
 *      champions, the registry with status and tier, decisions, issues.
 */

import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const INK = [15, 39, 64];        // PCG navy
const ACCENT = [217, 91, 26];    // PCG orange
const SOFT = [122, 136, 150];
const LINE = [226, 232, 240];

const fmt = (d) => { try { return d ? new Date(d).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : 'N/A'; } catch { return 'N/A'; } };
const humanize = (s) => String(s || '').replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

function header(doc, title, subtitle, orgName) {
  doc.setFillColor(...INK);
  doc.rect(0, 0, doc.internal.pageSize.getWidth(), 92, 'F');
  doc.setFillColor(...ACCENT);
  doc.rect(0, 0, doc.internal.pageSize.getWidth(), 4, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold'); doc.setFontSize(9);
  doc.text((orgName || 'PRECISION RESPONSIBLE AI').toUpperCase(), 40, 34);
  doc.setFontSize(17);
  doc.text(title, 40, 56, { maxWidth: doc.internal.pageSize.getWidth() - 80 });
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(200, 210, 220);
  doc.text(subtitle, 40, 76);
  return 112;
}

function sectionTitle(doc, y, text) {
  const pageH = doc.internal.pageSize.getHeight();
  if (y > pageH - 90) { doc.addPage(); y = 48; }
  doc.setFillColor(...ACCENT);
  doc.rect(40, y - 8, 3, 12, 'F');
  doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.setTextColor(...INK);
  doc.text(text.toUpperCase(), 50, y + 2);
  return y + 16;
}

function paragraph(doc, y, label, text, { maxW = 515 } = {}) {
  if (!text || !String(text).trim()) return y;
  const pageH = doc.internal.pageSize.getHeight();
  doc.setFont('helvetica', 'bold'); doc.setFontSize(8.5); doc.setTextColor(...SOFT);
  if (y > pageH - 80) { doc.addPage(); y = 48; }
  doc.text(label.toUpperCase(), 40, y);
  y += 11;
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9.5); doc.setTextColor(40, 50, 60);
  const lines = doc.splitTextToSize(String(text).trim(), maxW);
  for (const line of lines) {
    if (y > pageH - 56) { doc.addPage(); y = 48; }
    doc.text(line, 40, y); y += 12.5;
  }
  return y + 8;
}

function footerAll(doc, label) {
  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i += 1) {
    doc.setPage(i);
    const w = doc.internal.pageSize.getWidth(); const h = doc.internal.pageSize.getHeight();
    doc.setDrawColor(...LINE); doc.line(40, h - 34, w - 40, h - 34);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(...SOFT);
    doc.text(`${label} · Confidential`, 40, h - 22);
    doc.text(`Page ${i} of ${pages}`, w - 40, h - 22, { align: 'right' });
    doc.text('Format prepared by Precision Compliance Group', 40, h - 12);
  }
}

const TABLE_STYLE = {
  theme: 'plain',
  styles: { font: 'helvetica', fontSize: 8.5, textColor: [40, 50, 60], cellPadding: { top: 5, bottom: 5, left: 6, right: 6 }, lineColor: LINE, lineWidth: { bottom: 0.5 } },
  headStyles: { fontStyle: 'bold', fontSize: 7.5, textColor: SOFT, fillColor: [248, 250, 252] },
  margin: { left: 40, right: 40 },
};

export function exportUseCaseRecord({ useCase: uc, goldenRules = [], assessments = [], decisions = [], vendorChecks = [], issues = [], nist = [], securityReview = null, testRecords = [], readiness = null, monitoringReports = [], retirement = null, orgName = '' }) {
  const doc = new jsPDF({ unit: 'pt', format: 'letter' });
  let y = header(doc, uc.name, `AI governance record · ${humanize(uc.status)} · ${humanize(uc.risk_tier)} · Generated ${fmt(new Date())}`, orgName);

  y = sectionTitle(doc, y, 'Registry entry');
  autoTable(doc, {
    ...TABLE_STYLE, startY: y,
    body: [
      ['Kind', `${humanize(uc.kind)}${uc.vendor_name ? ` (${uc.vendor_name})` : ''}`],
      ['Department / owner', [uc.department, uc.owner_label].filter(Boolean).join(' · ') || 'N/A'],
      ['Proposed', `${fmt(uc.created_at)}${uc.proposed_by_email ? ` by ${uc.proposed_by_email}` : ''}`],
      ['Risk tier', humanize(uc.risk_tier)],
      ['Status', humanize(uc.status)],
      ['Re-review', uc.next_review_date ? `${fmt(uc.next_review_date)} (every ${uc.review_cadence_months} months)` : 'Set at approval'],
      ['Purpose', uc.purpose || 'N/A'],
      ['Model / system', uc.model_info || 'N/A'],
      ['Data in and out', uc.data_summary || 'N/A'],
      ['Intake screens', [
        `UM/coverage: ${uc.um_touching ? 'YES' : 'no'}`, `Member facing: ${uc.member_facing ? 'YES' : 'no'}`,
        `PHI: ${uc.phi_access ? 'YES' : 'no'}`, `Protected inputs: ${uc.uses_protected_inputs ? 'YES' : 'no'}`,
        `Autonomous: ${uc.autonomous_decisions ? 'YES' : 'no'}`, `Generative: ${uc.generative ? 'YES' : 'no'}`,
      ].join('   ')],
    ],
    columnStyles: { 0: { cellWidth: 120, fontStyle: 'bold', textColor: SOFT } },
  });
  y = doc.lastAutoTable.finalY + 18;

  if (goldenRules.length) {
    y = sectionTitle(doc, y, 'Non waivable rules this system engages');
    goldenRules.forEach((r, i) => { y = paragraph(doc, y, `Rule ${i + 1}`, r); });
  }

  const current = assessments.find((a) => a.is_current) || assessments[0];
  if (current) {
    y = sectionTitle(doc, y, `Impact assessment of record (version ${current.version_number}, ${fmt(current.created_at)})`);
    y = paragraph(doc, y, 'Summary', current.narrative);
    y = paragraph(doc, y, 'Residual risk and recommendation', [current.risk_rating && `Residual risk: ${current.risk_rating}.`, current.recommendation && `Recommendation: ${humanize(current.recommendation)}.`].filter(Boolean).join(' '));
    [['intended_use', 'Intended purpose'], ['benefits', 'Expected benefits'], ['tool_type', 'Type of tool'], ['autonomy_explainability', 'Degree of autonomy and explainability'],
     ['harms', 'Foreseeable risks and who could be harmed'], ['affected_populations', 'Members, providers and staff affected'], ['data_inputs', 'Data used'], ['human_oversight', 'Human oversight'],
     ['transparency', 'Required disclosures'], ['security_privacy', 'Security and privacy safeguards'],
     ['bias_testing', 'Bias and fairness testing'], ['performance_metrics', 'Performance metrics'], ['mitigation_plan', 'Mitigation plan'], ['backup_plan', 'Backup Plan'],
     ...(uc.kind === 'vendor_tool' ? [['vendor_joint_monitoring', 'Joint monitoring with the vendor']] : []),
     ['monitoring_plan', 'Monitoring plan'], ['regulatory_notes', 'Applicable requirements']]
      .forEach(([k, label]) => { y = paragraph(doc, y, label, k === 'tool_type' ? humanize(current[k]) : current[k]); });
  }

  // COM028 Section 5: the security review of record.
  y = sectionTitle(doc, y, 'Security review (COM028 Section 5)');
  if (securityReview) {
    autoTable(doc, {
      ...TABLE_STYLE, startY: y,
      body: [
        ['Standing', securityReview.status === 'complete' ? `Complete, ${fmt(securityReview.reviewed_at)} by ${securityReview.reviewed_by_label || 'the security member'}` : 'Pending'],
        ['User access and permissions', securityReview.user_access || 'N/A'],
        ['Data storage and processing', securityReview.data_location || 'N/A'],
        ['Retention', securityReview.retention || 'N/A'],
        ['Outside the United States', securityReview.offshore === true ? 'Yes' : securityReview.offshore === false ? 'No' : 'N/A'],
        ['Our data used for training', securityReview.trains_on_org_data === true ? `Yes. ${securityReview.training_notes || ''}` : securityReview.trains_on_org_data === false ? 'No' : 'N/A'],
        ['Logging', securityReview.logging || 'N/A'],
        ...(uc.kind === 'vendor_tool' ? [['Vendor certifications and incident reporting', securityReview.vendor_security || 'N/A']] : []),
        ['Security conditions', securityReview.conditions || 'None'],
      ],
      columnStyles: { 0: { cellWidth: 150, fontStyle: 'bold', textColor: SOFT } },
    });
    y = doc.lastAutoTable.finalY + 18;
  } else { y = paragraph(doc, y, 'Standing', 'No security review on the record.'); }

  // COM028 Section 6: pre implementation testing.
  y = sectionTitle(doc, y, 'Pre implementation testing (COM028 Section 6)');
  if (testRecords.length) {
    const t = testRecords[0];
    y = paragraph(doc, y, 'Latest run', `${humanize(t.outcome)}, tested ${fmt(t.tested_on || t.created_at)}, recorded by ${t.recorded_by_label || 'compliance'}.${t.environment ? ` Environment: ${t.environment}.` : ''}${t.data_used ? ` Data: ${t.data_used}.` : ''}${t.vendor_testing ? ` Vendor testing relied on: ${t.vendor_testing_basis || ''}` : ''}${t.variance_control ? ` Variance control: ${t.variance_control}` : ''}`);
    autoTable(doc, {
      ...TABLE_STYLE, startY: y,
      head: [['Metric', 'Target', 'Result', 'Met']],
      body: (t.metrics || []).map((m) => [m.metric, m.target, m.result, m.met ? 'Yes' : 'No']),
      columnStyles: { 3: { cellWidth: 40 } },
    });
    y = doc.lastAutoTable.finalY + 18;
    if (testRecords.length > 1) y = paragraph(doc, y, 'Earlier runs', testRecords.slice(1).map((r) => `${fmt(r.tested_on || r.created_at)}: ${humanize(r.outcome)}`).join('; '));
  } else { y = paragraph(doc, y, 'Standing', 'No test record on the record.'); }

  if (readiness && readiness.items && readiness.items.length) {
    y = sectionTitle(doc, y, 'Readiness for a committee decision (COM028 Section 7)');
    autoTable(doc, {
      ...TABLE_STYLE, startY: y,
      head: [['Requirement', 'Standing', 'On this record']],
      body: readiness.items.map((i) => [i.label, i.met ? 'In place' : 'Owed', i.note]),
      columnStyles: { 0: { cellWidth: 190 }, 1: { cellWidth: 55 } },
    });
    y = doc.lastAutoTable.finalY + 18;
  }

  y = sectionTitle(doc, y, 'Committee decisions');
  if (decisions.length) {
    autoTable(doc, {
      ...TABLE_STYLE, startY: y,
      head: [['Decision', 'Date', 'By', 'On version', 'Notes']],
      body: decisions.map((d) => [
        humanize(d.decision) + (d.self_review ? ' (sole officer review, recorded)' : ''),
        fmt(d.decided_at), d.decided_by_label || 'Committee',
        d.assessment_version ? `v${d.assessment_version}` : 'N/A', d.notes || '',
      ]),
      columnStyles: { 0: { cellWidth: 130 }, 1: { cellWidth: 80 }, 2: { cellWidth: 100 }, 3: { cellWidth: 55 } },
    });
    y = doc.lastAutoTable.finalY + 12;
    for (const d of decisions) {
      const cons = Array.isArray(d.contingencies) ? d.contingencies : [];
      if (cons.length) {
        y = paragraph(doc, y, `Contingencies of the ${fmt(d.decided_at)} decision`,
          cons.map((c, i) => `${i + 1}. [${c.done ? 'DONE' : 'OPEN'}] ${c.item}${c.owner ? ` (${c.owner})` : ''}`).join('\n'));
      }
    }
  } else { y = paragraph(doc, y, 'Status', 'No committee decision recorded yet.'); }

  if (vendorChecks.length) {
    y = sectionTitle(doc, y, 'Vendor evidence dossier');
    autoTable(doc, {
      ...TABLE_STYLE, startY: y,
      head: [['Item', 'Status', 'Evidence']],
      body: vendorChecks.map((c) => [humanize(c.item), humanize(c.status), c.evidence || '']),
      columnStyles: { 0: { cellWidth: 170 }, 1: { cellWidth: 70 } },
    });
    y = doc.lastAutoTable.finalY + 18;
  }

  y = sectionTitle(doc, y, 'Documented issues');
  if (issues.length) {
    autoTable(doc, {
      ...TABLE_STYLE, startY: y,
      head: [['Severity', 'Issue', 'Status', 'Found', 'Resolution']],
      body: issues.map((i) => [i.severity.toUpperCase(), i.title, humanize(i.status), fmt(i.found_at), i.resolution || '']),
      columnStyles: { 0: { cellWidth: 60 }, 2: { cellWidth: 60 }, 3: { cellWidth: 80 } },
    });
    y = doc.lastAutoTable.finalY + 18;
  } else { y = paragraph(doc, y, 'Status', 'No issues documented against this system.'); }

  if (monitoringReports.length) {
    y = sectionTitle(doc, y, 'Monitoring reports (COM028 Sec 9)');
    autoTable(doc, {
      ...TABLE_STYLE, startY: y,
      head: [['Submitted', 'Period', 'Metrics met', 'Critical errors', 'Material change', 'Compliance review']],
      body: monitoringReports.map((r) => {
        const ms = Array.isArray(r.metrics) ? r.metrics : [];
        return [`${fmt(r.submitted_at)} by ${r.submitted_by_label || ''}`, r.period_label || '', ms.length ? `${ms.filter((m) => m.met).length}/${ms.length}` : 'in words', String(r.critical_errors || 0), r.material_change ? 'YES' : 'no', r.status === 'reviewed' ? `${fmt(r.reviewed_at)}${r.review_note ? `: ${r.review_note}` : ''}` : 'awaiting review'];
      }),
      columnStyles: { 0: { cellWidth: 110 }, 2: { cellWidth: 60 }, 3: { cellWidth: 55 }, 4: { cellWidth: 60 } },
    });
    y = doc.lastAutoTable.finalY + 18;
  }
  if (uc.material_change_at) {
    y = paragraph(doc, y, 'Material Change', `Reported ${fmt(uc.material_change_at)}. ${uc.material_change_note || ''}`);
  }
  if (uc.retirement_requested_at || uc.status === 'retired') {
    y = sectionTitle(doc, y, 'Retirement (COM028 Sec 11)');
    y = paragraph(doc, y, uc.status === 'retired' ? `Retired ${fmt(uc.retired_at)}` : `Requested ${fmt(uc.retirement_requested_at)}`,
      [uc.retirement_reason, ...((retirement && retirement.lines) || []).map((l) => `[${l.done ? 'DONE' : 'OPEN'}] ${l.label}${l.done ? ` (${l.by_label || ''}, ${fmt(l.at)})` : ''}`), 'The record is retained in the AI inventory.'].filter(Boolean).join('\n'));
  }

  if (nist.length) {
    y = sectionTitle(doc, y, 'NIST AI RMF alignment');
    autoTable(doc, {
      ...TABLE_STYLE, startY: y,
      head: [['Function', 'Control theme', 'Standing', 'On this record']],
      body: nist.map((n) => [n.fn, n.theme, n.met ? 'In place' : 'Owed', n.note]),
      columnStyles: { 0: { cellWidth: 60 }, 1: { cellWidth: 130 }, 2: { cellWidth: 55 } },
    });
  }

  footerAll(doc, `${uc.name} · AI governance record`);
  doc.save(`AI_Governance_Record_${String(uc.name).replace(/[^A-Za-z0-9]+/g, '_').slice(0, 50)}.pdf`);
}

export function exportProgramReport({ report, orgName = '' }) {
  const doc = new jsPDF({ unit: 'pt', format: 'letter' });
  const cases = report.use_cases || [];
  let y = header(doc, 'Responsible AI Program Report',
    `${cases.length} registered system${cases.length === 1 ? '' : 's'} · Generated ${fmt(report.generated_at)}`, orgName);

  y = sectionTitle(doc, y, 'Program structure');
  y = paragraph(doc, y, 'Governance committee',
    (report.committee || []).length ? report.committee.map((m) => `${m.member_label} (${m.seat})`).join('; ') : 'No seats named.');
  y = paragraph(doc, y, 'Department champions',
    (report.champions || []).length ? report.champions.map((c) => `${c.champion_label} (${c.department})`).join('; ') : 'No champions named.');
  y = paragraph(doc, y, 'How the program works',
    'Every AI function the organization builds or buys is registered before development or purchase, screened at intake, and risk tiered by fixed rules. ' +
    'Full review systems reach the governance committee with a versioned impact assessment; the committee decision is recorded permanently with any contingencies tracked to completion. ' +
    'Vendor systems carry an evidence dossier. Champions in each department bring AI to intake, anyone may document an issue found in production, and every approved system carries a re-review date.');

  y = sectionTitle(doc, y, 'The registry');
  autoTable(doc, {
    ...TABLE_STYLE, startY: y,
    head: [['System', 'Kind', 'Tier', 'Status', 'Dept', 'Re-review', 'Open issues']],
    body: cases.map((uc) => [
      uc.name, humanize(uc.kind), humanize(uc.risk_tier), humanize(uc.status),
      uc.department || '', uc.next_review_date ? fmt(uc.next_review_date) : '',
      String((report.issues || []).filter((i) => i.use_case_id === uc.id && i.status === 'open').length),
    ]),
    columnStyles: { 0: { cellWidth: 150 }, 6: { cellWidth: 50 } },
  });
  y = doc.lastAutoTable.finalY + 18;

  y = sectionTitle(doc, y, 'Decisions on the record');
  const decs = report.decisions || [];
  if (decs.length) {
    autoTable(doc, {
      ...TABLE_STYLE, startY: y,
      head: [['Date', 'System', 'Decision', 'By']],
      body: decs.map((d) => {
        const uc = cases.find((c) => c.id === d.use_case_id);
        return [fmt(d.decided_at), uc ? uc.name : d.use_case_id, humanize(d.decision) + (d.self_review ? ' (sole officer, recorded)' : ''), d.decided_by_label || 'Committee'];
      }),
      columnStyles: { 0: { cellWidth: 80 }, 2: { cellWidth: 170 } },
    });
    y = doc.lastAutoTable.finalY + 18;
  } else { y = paragraph(doc, y, 'Status', 'No decisions recorded yet.'); }

  y = sectionTitle(doc, y, 'Issue log');
  const iss = report.issues || [];
  if (iss.length) {
    autoTable(doc, {
      ...TABLE_STYLE, startY: y,
      head: [['Found', 'System', 'Severity', 'Issue', 'Status']],
      body: iss.map((i) => {
        const uc = cases.find((c) => c.id === i.use_case_id);
        return [fmt(i.found_at), uc ? uc.name : '', i.severity.toUpperCase(), i.title, humanize(i.status)];
      }),
      columnStyles: { 0: { cellWidth: 75 }, 2: { cellWidth: 60 }, 4: { cellWidth: 60 } },
    });
  } else { y = paragraph(doc, y, 'Status', 'No issues documented across the program.'); }

  footerAll(doc, 'Responsible AI Program Report');
  doc.save('Responsible_AI_Program_Report.pdf');
}
